import type { Path } from 'react-hook-form';
import { z } from 'zod';
import { FormField, type FormSchema, type ZodForm } from './Form';
import { Select, type SelectOption } from './Select';

/** Every status a driver record can hold, as the API spells them. */
export const DRIVER_STATUSES = [
  'INVITED',
  'ACTIVE',
  'CLEAR',
  'DISPATCHED',
  'ON_LEAVE',
  'WAITING',
  'EN_ROUTE',
  'ON_SCENE',
  'TOWING',
  'RETURNING',
  'OFFLINE',
] as const;

export type DriverStatus = (typeof DRIVER_STATUSES)[number];

/** The statuses a dispatcher can set by hand on the edit dialogs. */
export type EditableDriverStatus = Exclude<DriverStatus, 'INVITED' | 'CLEAR'>;

export interface DriverTruckOption {
  id: string;
  /** The whole line for the option — number, current driver, rating. */
  label: string;
}

/**
 * The two fields as a schema fragment, for a form to spread into its own:
 *
 *   z.object({ name: …, ...driverTruckStatusShape })
 *
 * The status accepts every value the record can already hold, so a driver
 * who is still INVITED can have their truck changed without first being
 * forced onto a status the dispatcher did not choose. The select only
 * offers the editable ones.
 */
export const driverTruckStatusShape = {
  /** The chosen truck, or none. */
  truckId: z.string().optional(),
  status: z.enum(DRIVER_STATUSES).default('ACTIVE'),
};

export const DRIVER_STATUS_OPTIONS: SelectOption[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ON_LEAVE', label: 'On Leave' },
  { value: 'DISPATCHED', label: 'Dispatched' },
  { value: 'WAITING', label: 'Waiting' },
  { value: 'EN_ROUTE', label: 'En Route' },
  { value: 'ON_SCENE', label: 'On Scene' },
  { value: 'TOWING', label: 'Towing' },
  { value: 'RETURNING', label: 'Returning' },
  { value: 'OFFLINE', label: 'Offline' },
];

/**
 * Radix reserves the empty string for the cleared state and throws if an
 * item uses it, so "no truck" needs a real value — translated back to
 * undefined on the way out.
 */
const NO_TRUCK = '__none__';

export interface DriverTruckStatusFieldsProps<S extends FormSchema> {
  form: ZodForm<S>;
  /** Which of the form's fields hold the truck and the status. */
  truckName: Path<z.infer<S>>;
  statusName: Path<z.infer<S>>;
  trucks: DriverTruckOption[];
  /** The list is still on its way: the trigger says so. */
  trucksLoading?: boolean;
}

/**
 * The two fields the edit dialogs share: which truck a driver is in, with
 * "None" leading the list, and their status. Bound to the form they sit
 * in, and generic over it because a form's type is its whole schema —
 * the caller says which two fields are meant.
 *
 * Built by EditDriverDialogView; not on the
 * public surface.
 */
export function DriverTruckStatusFields<S extends FormSchema>({
  form,
  truckName,
  statusName,
  trucks,
  trucksLoading = false,
}: DriverTruckStatusFieldsProps<S>) {
  const truckOptions: SelectOption[] = [
    { value: NO_TRUCK, label: 'None' },
    ...trucks.map((truck) => ({ value: truck.id, label: truck.label })),
  ];
  return (
    <>
      <FormField form={form} name={truckName} label="Truck">
        {(field) => (
          <Select
            options={truckOptions}
            placeholder={trucksLoading ? 'Loading trucks...' : 'Select a truck'}
            value={field.value || NO_TRUCK}
            onValueChange={(value) =>
              field.onChange(value === NO_TRUCK ? undefined : value)
            }
          />
        )}
      </FormField>
      <FormField form={form} name={statusName} label="Status">
        {(field) => (
          <Select
            options={DRIVER_STATUS_OPTIONS}
            placeholder="Select status"
            value={field.value}
            onValueChange={field.onChange}
          />
        )}
      </FormField>
    </>
  );
}
